export interface UserProfile {
  email: string;
  body: string;
  identity: string;
  access: string[];
  modules: { [key: string]: string[] };
}

export interface Amil {
  name: string;
  ic: string;
  hash: string[];
  email: string;
  body: string;
  code: string;
  data: string;
  total_collected: number;
  amount_collected: number;
  kariah_code: string;
  kariah_name: string;
}

export type PaymentReport =
  | { status: "error"; reason: string }
  | { status: "OK"; data: CollectionFitrah[]; stats: CollectionFitrahSummary };

export interface FPXRequestReport {
  status: string;
  fpxrequest: FPXRequest;
}

export interface FPXRequest {
  fpx_seller_txn_time: string;
  fpx_seller_ex_order_no: string;
  fpx_seller_order_no: string;
  fpx_buyer_email: string;
  agency: string;
  ref_no: string;
  akaun_no: string;
  payment_category: string;
  fpx_txn_amount: string;
  fpx_buyer_bank_id: string;
  fpx_buyer_bank_name: string;
  fpx_debit_auth_code: string;
  fpx_debit_auth_code_desc: string;
  extra_data?: string;
  add_charge?: string;
}

export interface FPXRequestStats {
  page: number;
  items_per_page: number;
  count: number;
  total_pages: number;
  txn_amount: number;
  txn_charges: number;
  txn_summary: SummaryStats[];
}

export interface SummaryStats {
  name: string;
  count: number;
  txn_amount: number;
  txn_charges: number;
}

export interface CollectionFitrah {
  id: number;
  datetime: string;
  ref_no: string;
  agency: string;
  currency: string;
  paid_amount: number;
  charge: number;
  amount: number;
  amil_amount: number;
  body_amount: number;
  payer_email: string;
  payer_phone: string;
  payer_ic: string;
  payer_name: string;
  dependant: number;
  rate: number;
  is_approved: boolean;
  reason: string;
  amil_ic: string;
  amil_name: string;
  address: string;
  postcode: string;
  zone: string;
  state: string;
}

export interface CollectionFitrahSummary {
  page: number;
  items_per_page: number;
  count: number;
  total_pages: number;
  paid_amount: number;
  charges: number;
  amount: number;
  amil_amount: number;
  body_amount: number;
}

export interface SelectionAmil {
  ic: string;
  name: string;
  body: string;
}

export interface SelectionKariah {
  body: string;
  code: string;
  name: string;
}

export type ResponseData =
  | UserProfile
  | Amil[]
  | boolean
  | SelectionAmil[]
  | SelectionKariah[];

export type Response =
  | { status: "error"; reason: string }
  | { status: "OK"; data: ResponseData };

export const fetchData = <T extends ResponseData>(endpoint: string) => (args?: {
  [key: string]: string;
}) => async (token: string): Promise<T> => {
  try {
    let target = endpoint;

    let isDownload = false;

    if (args) {
      const params = new URLSearchParams();
      for (const key in args) {
        if (key === "download") {
          isDownload = true;
        }
        params.set(key, args[key]);
      }
      target = `${target}?${params.toString()}`;
    }

    const resp = await fetch(target, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (resp.status === 200) {
      if (isDownload) {
        const data = new Blob([await resp.blob()], { type: "text/csv" });
        const link = window.URL.createObjectURL(data);
        const temp = document.createElement("a");
        temp.href = link;
        temp.setAttribute("download", "laporan.csv");
        temp.click();

        window.URL.revokeObjectURL(link);

        throw new Error("downloaded");
      } else {
        const data = (await resp.json()) as Response;

        switch (data.status) {
          case "OK":
            return data.data as T;
          case "error":
            throw new Error(data.reason);
          default:
            throw new Error("unknown response");
        }
      }
    } else {
      throw new Error("unable to fetch data");
    }
  } catch (e) {
    console.log(e);
    throw e;
  }
};

const profileEndpoint = "/api/v1/whoami/";
export const fetchUserProfile = fetchData<UserProfile>(profileEndpoint)();

const amilListEndpoint = "/api/v1/report/amil/";
export const fetchAmilList = fetchData<Amil[]>(amilListEndpoint);

const selectionAmilEndpoint = "/api/v1/selection/amil/";
export const fetchSelectionAmil = fetchData<SelectionAmil[]>(
  selectionAmilEndpoint
);

const selectionKariahEndpoint = "/api/v1/selection/kariah/";
export const fetchSelectionKariah = fetchData<SelectionKariah[]>(
  selectionKariahEndpoint
);

const collectionFitrahEndpoint = "/api/v1/report/collection/fitrah/";
export const fetchCollectionFitrah = (args?: {
  [key: string]: string;
}) => async (token: string): Promise<PaymentReport> => {
  try {
    let target = collectionFitrahEndpoint;

    if (args) {
      const params = new URLSearchParams();
      for (const key in args) {
        params.set(key, args[key]);
      }
      target = `${target}?${params.toString()}`;
    }

    const resp = await fetch(target, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (resp.status === 200) {
      const data = (await resp.json()) as PaymentReport;

      switch (data.status) {
        case "OK":
          return data;
        case "error":
          throw new Error(data.reason);
        default:
          throw new Error("unknown response");
      }
    }

    throw new Error("unable to fetch data");
  } catch (e) {
    console.log(e);
    throw e;
  }
};
